import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { customValidators, array as yupArray } from '../../../../Helpers/Validators/customValidators';
import { object, string } from 'yup';
import InsuredBasicInfoForm from '../SubComponents/InsuredBasicInfoForm';
import Age from '../../../../Helpers/Dates/Age';
import BackNextButton from '../../../../Common/BackNextButton';
import { SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem } from '../../../../../sessionStorage';

export default function InsuredBasicInfo(props) {
    const [isBirthDateError, setIsBirthDateError] = useState('');
    const [isInsuredRelationshipWithOwnerDisabled, setIsInsuredRelationshipWithOwnerDisabled] = useState(false);
    const isProposedInsured = props.fullFormState.pages.retrieveAccount.isProposedInsured;
    const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    const isMSEnableRelationshipToOwner = accountObj && accountObj.msEnableRelationshipToOwner;

    const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
        validationSchema: InsuredBasicInfo.ValidationSchema(props.ageSpecs, props.isSurvivorshipPolicy, isMSEnableRelationshipToOwner),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    useEffect(() => {
        window.scroll(0, 0);
        if (isProposedInsured === "true" && isMSEnableRelationshipToOwner) {
            props.fullFormState.pages.insuredBasicInfo.insuredInfo[0].insuredRelationshipWithOwner = 'Self';
            setValue(`insuredInfo[${0}].insuredRelationshipWithOwner`, 'Self');
            setIsInsuredRelationshipWithOwnerDisabled(true);
        }
    }, []);

    useEffect(() => {
        const listOfFields = ['proposedInsuredFirstName', 'proposedInsuredMiddleInitial', 'proposedInsuredLastName', 'gender', 'birthdate', 'maritalStatus', 'tobacco', 'state', 'county', 'proposedInsuredEmail', 'proposedInsuredPhone', 'isOther', 'insuredRelationshipWithOwner']
        props.formState.insuredInfo.forEach((basicInfo, index) => {
            listOfFields.map((field) => {
                register({ name: `insuredInfo[${index}].${field}` });
                setValue(`insuredInfo[${index}].${field}`, basicInfo[field]);
            })
        });
    }, [register]);

    const handleOnNext = () => {
        if (!props.isSurvivorshipPolicy) {
            props.onNext();
            return;
        }

        setIsBirthDateError(false);

        const [insured1, insured2] = props.formState.insuredInfo || [];
        const getAge = (birthdate) => Age(birthdate);
        const insured1age = getAge(insured1.birthdate);
        const insured2age = getAge(insured2.birthdate);
        const ageDifference = Math.abs(insured1age - insured2age);
        if (ageDifference > 25) {
            setIsBirthDateError(true);
            return
        }
        else {
            props.onNext()
        }
    }

    return (
        <Container fluid className="border-class">
            <form onSubmit={handleSubmit(handleOnNext)}>
                <div className="m-2 p-2">
                    <Row className="justify-content-center">
                        <Col lg={4}>
                            {props.formState.insuredInfo.map((item, key) => {
                                return (
                                    <InsuredBasicInfoForm
                                        item={item}
                                        key={key}
                                        index={key}
                                        formState={props.formState}
                                        fullFormState={props.fullFormState}
                                        errors={errors}
                                        onFieldChange={(field, value) => props.onFieldChange(field, value)}
                                        register={register}
                                        setValue={setValue}
                                        isBirthDateError={isBirthDateError}
                                        setIsBirthDateError={setIsBirthDateError}
                                        isSurvivorshipPolicy={props.isSurvivorshipPolicy}
                                        triggerValidation={triggerValidation}
                                        isMSEnableRelationshipToOwner={isMSEnableRelationshipToOwner}
                                        isInsuredRelationshipWithOwnerDisabled={isInsuredRelationshipWithOwnerDisabled}
                                        onChange={(newForm) => {
                                            props.onFieldChange(
                                                'insuredInfo',
                                                props.formState.insuredInfo.map((form, formIndex) => {
                                                    return formIndex === key ? newForm : form;
                                                })
                                            );
                                        }}
                                    />
                                )
                            })}
                        </Col>
                    </Row>
                </div>
                <BackNextButton onBack={props.onBack} />
            </form>
        </Container>
    );
}

InsuredBasicInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    ageSpecs: PropTypes.object,
    showCounty: PropTypes.bool,
};

InsuredBasicInfo.ValidationSchema = (ageSpecs = {}, isSurvivorshipPolicy = false, isMSEnableRelationshipToOwner = false) => {

    return object().shape({
        insuredInfo: yupArray(object().shape({
            proposedInsuredFirstName: customValidators.string().isValidNameLimitedSpecialChar('First Name is required.', true, false),
            proposedInsuredMiddleInitial: customValidators.string()
                .matches(/^[()!#&*+,.\/:;=?@^_`|~a-zA-ZÀ-ÖØ-öø-ÿ'-]{0,1}$/, 'Must be single and valid alpha character.').notRequired(),
            proposedInsuredLastName: customValidators.string().isValidNameLimitedSpecialChar('Last Name is required.', true, false),
            gender: string().required("Gender is required."),
            birthdate: isSurvivorshipPolicy ?
                customValidators.string().isValidateAgeAndDifferenceFromBirthdate('Invalid Date.', ageSpecs.min, 70,
                    "CareMatters Together issue age range is 30 - 70 years. Please contact your Covr sales team for assistance.")
                : customValidators.string().isValidateAgeAndDifferenceFromBirthdate('Invalid Date.', ageSpecs.min, ageSpecs.max,
                    `This product is only available from age ${ageSpecs.min} to ${ageSpecs.max}.`),
            state: string().required("State is required."),
            maritalStatus: string().required('Marital status is required.'),
            county: string().when('state', {
                is: (state) => (state !== undefined && state === "CA"),
                then: string().required('County is required.'),
                otherwise: string().notRequired(),
            }),
            tobacco: string().required('This field is required.'),
            proposedInsuredEmail: string().when('isOther', {
                is: true,
                then: string().email('Email address must be valid.').required('Email address is required.'),
                otherwise: string().notRequired(),
            }),
            proposedInsuredPhone: string().when('isOther', {
                is: true,
                then: string().required('Phone Number is required.').matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890'),
                otherwise: string().notRequired(),
            }),
            insuredRelationshipWithOwner: !isSurvivorshipPolicy && isMSEnableRelationshipToOwner ? string().required('Relationship with owner is required.').nullable() : string().notRequired(),
        })),
    });
};

InsuredBasicInfo.DefaultFormState = (prefill = false) => {
    return {
        insuredInfo: [{
            proposedInsuredFirstName: prefill ? '' : '',
            proposedInsuredLastName: prefill ? '' : '',
            proposedInsuredMiddleInitial: prefill ? '' : '',
            gender: prefill ? '' : '',
            birthdate: prefill ? '' : '',
            state: prefill ? '' : '',
            maritalStatus: prefill ? '' : '',
            tobacco: prefill ? '' : '',
            county: prefill ? '' : '',
            proposedInsuredEmail: prefill ? '' : '',
            proposedInsuredPhone: prefill ? '' : '',
            isOther: true,
            insuredRelationshipWithOwner: prefill ? '' : '',
        }],
    }
};



// WEBPACK FOOTER //
// ./src/components/Product/ABLTC/RequestIllustration/Steps/InsuredBasicInfo.js